import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert, Snackbar } from '@mui/material';

import { temperatureContext } from '../../../lib/context';
import { convertTemperature, convertToCelsius, convertToSecond, secondToTime } from '../../../lib/convert';
import { NotificationsNotSavedError, useUpdateAlarmConfig } from '../api/mutations';

const DAY_MINUTES = 24 * 60;
const WINDOW_STEP = 15; // minute snap granularity for the working-time slider

// Offline-notification: value is a multiple of 30 minutes. The backend offline check
// runs every 5 minutes and needs a 30-min data-delay buffer, so 30 min is the smallest
// interval where the first alert lands roughly on the configured threshold. Default
// is 6 hours. Persisted as seconds in miss_time_limit.
const OFFLINE_STEP_MIN = 30;
const DEFAULT_OFFLINE_MIN = 6 * 60;

// The window is evaluated in the device user's local time; record the IANA zone so a
// weekday check can't drift across midnight. Falls back to the browser's zone.
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const formatClock = (minutes: number) => {
    const total = ((Math.round(minutes) % (DAY_MINUTES + 1)) + DAY_MINUTES + 1) % (DAY_MINUTES + 1);
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Format an IANA zone as "Asia/Taipei · GMT+8" for display next to the working-time
// inputs, so users understand what "09:00" refers to. Falls back to the raw zone name
// if the runtime can't produce a short offset.
const formatTimezoneDisplay = (zone: string) => {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: zone,
            timeZoneName: 'shortOffset',
        }).formatToParts(new Date());
        const offset = parts.find((p: any) => p.type === 'timeZoneName')?.value;
        return offset ? `${zone} · ${offset}` : zone;
    } catch {
        return zone;
    }
};

// Parse a typed "H:MM"/"HH:MM" (also "HHMM") into clamped minutes, or null if invalid.
const parseClock = (raw: any) => {
    const match = String(raw)
        .trim()
        .match(/^(\d{1,2})\s*:?\s*(\d{2})$/);
    if (!match) return null;
    const minute = Number(match[2]);
    if (minute > 59) return null;
    return Math.min(DAY_MINUTES, Math.max(0, Number(match[1]) * 60 + minute));
};

function AlarmConfigForm({
    record,
    compact = false,
    onClose,
    isArchived = false,
    onToggleArchive,
    archiving = false,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime.Edit' });
    const { t: tNotify } = useTranslation(undefined, { keyPrefix: 'Notification' });
    const updateAlarmConfig = useUpdateAlarmConfig();
    const tempUnit = useContext(temperatureContext);

    const intervalConvert = secondToTime(record.interval);

    // The non-working (default) threshold set is the device's top-level threshold
    // (record.upper/lower/timeLimit). The working-hours set is nested and may be
    // absent on devices that never configured it.
    const workingHours = record.workingHours ?? {};

    // Effective IANA zone for the window: whatever was saved previously, else the
    // browser's zone. Kept in one place so the caption under the slider and the save
    // payload stay in lockstep (a mismatch would silently ship a different schedule
    // than the user sees).
    const effectiveTimezone = workingHours.timezone || LOCAL_TZ;

    const workThresholdConvert = secondToTime(workingHours.timeLimit ?? record.timeLimit);
    const offThresholdConvert = secondToTime(record.timeLimit);

    // null = closed, otherwise the outcome of the last save.
    const [saveAlert, setSaveAlert] = useState<'success' | 'warning' | 'error' | null>(null);

    const [inAppNotification, setInAppNotification] = useState(!!record.appNotification);
    const [emailEnabled, setEmailEnabled] = useState(!!record.emailNotification);
    const [lineEnabled, setLineEnabled] = useState(!!record.lineNotification);

    // Whether the working-hours threshold set is active. When off, the base
    // (non-working) set applies at all times.
    const [workingEnabled, setWorkingEnabled] = useState(record.workingHours?.enabled ?? false);
    const [activeShift, setActiveShift] = useState(record.workingHours?.enabled ? 'working' : 'nonWorking');
    // Working-time window (shared across both tabs). Minutes from midnight, parsed
    // from the stored HH:mm; defaults to 09:00–18:00 until a device configures it.
    const [workStart, setWorkStart] = useState(parseClock(record.workingHours?.startTime) ?? 9 * 60);
    const [workEnd, setWorkEnd] = useState(parseClock(record.workingHours?.endTime) ?? 18 * 60);
    // 'weekdays' = Mon–Fri, 'everyday' = Mon–Sun. Shared across both tabs.
    const [workDays, setWorkDays] = useState(record.workingHours?.days === 'everyday' ? 'everyday' : 'weekdays');
    // Offline-notification delay in minutes (multiple of OFFLINE_STEP_MIN). Persisted
    // as seconds. Snap legacy values (previously stored in 5-min increments) up to the
    // nearest 30-min step so the stepper starts on a valid position.
    const [offlineMinutes, setOfflineMinutes] = useState(
        Number.isFinite(record.missTimeLimit)
            ? Math.max(OFFLINE_STEP_MIN, Math.round(record.missTimeLimit / 60 / OFFLINE_STEP_MIN) * OFFLINE_STEP_MIN)
            : DEFAULT_OFFLINE_MIN
    );
    const [upperUnit, setUpperUnit] = useState(tempUnit);
    const [lowerUnit, setLowerUnit] = useState(tempUnit);
    const [workTimeUnit, setWorkTimeUnit] = useState(workThresholdConvert.unit);
    const [offTimeUnit, setOffTimeUnit] = useState(offThresholdConvert.unit);
    const [intervalUnit] = useState(intervalConvert.unit);

    const workUpLimitInput = useRef<HTMLInputElement>(null);
    const workLowLimitInput = useRef<HTMLInputElement>(null);
    const workTimeLimitInput = useRef<HTMLInputElement>(null);
    const offUpLimitInput = useRef<HTMLInputElement>(null);
    const offLowLimitInput = useRef<HTMLInputElement>(null);
    const offTimeLimitInput = useRef<HTMLInputElement>(null);
    const intervalInput = useRef<HTMLInputElement>(null);
    const emailInput = useRef<HTMLInputElement>(null);
    const emailInput2 = useRef<HTMLInputElement>(null);
    const emailInput3 = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setUpperUnit(tempUnit);
        setLowerUnit(tempUnit);
    }, [tempUnit]);

    const handleCloseSaveAlert = (event?: any, reason?: any) => {
        if (reason === 'clickaway') {
            return;
        }
        setSaveAlert(null);
    };

    const handleToggleApp = (enabled: any) => {
        setInAppNotification(enabled);
    };

    const handleToggleEmail = (enabled: any) => {
        setEmailEnabled(enabled);
    };

    const handleToggleLine = (enabled: any) => {
        setLineEnabled(enabled);
    };

    const handleSaveAll = () => {
        const notify_emails = emailEnabled
            ? [emailInput.current?.value, emailInput2.current?.value, emailInput3.current?.value].filter(Boolean)
            : record.notifyEmails;

        const buildGroup = (upRef: any, lowRef: any, timeRef: any, timeUnit: any) => ({
            upper_limit: convertToCelsius(parseFloat(upRef.current.value), upperUnit),
            lower_limit: convertToCelsius(parseFloat(lowRef.current.value), lowerUnit),
            time_limit: convertToSecond(parseInt(timeRef.current.value, 10), timeUnit),
        });

        const working = buildGroup(workUpLimitInput, workLowLimitInput, workTimeLimitInput, workTimeUnit);
        const nonWorking = buildGroup(offUpLimitInput, offLowLimitInput, offTimeLimitInput, offTimeUnit);

        // Window endpoints as HH:mm; 24:00 (1440) wraps to "00:00" (overnight = end < start).
        const toHHmm = (minutes: number) => formatClock(minutes % DAY_MINUTES);

        const newConfig = {
            // Non-working (default/base) set → device top-level threshold. Excursion
            // type is always Single Event, so no `type` field is sent.
            upper_limit: nonWorking.upper_limit,
            lower_limit: nonWorking.lower_limit,
            time_limit: nonWorking.time_limit,
            // Offline-disconnect notification threshold (seconds, multiple of 300).
            miss_time_limit: offlineMinutes * 60,
            working_hours: {
                enabled: workingEnabled,
                days: workDays,
                start_time: toHHmm(workStart),
                end_time: toHHmm(workEnd),
                timezone: effectiveTimezone,
                upper_limit: working.upper_limit,
                lower_limit: working.lower_limit,
                time_limit: working.time_limit,
            },
            notify_emails,
        };

        updateAlarmConfig.mutate(
            {
                deviceId: record.deviceId,
                config: newConfig,
                notifications: { app: inAppNotification, email: emailEnabled, line: lineEnabled },
            },
            {
                onSuccess: () => {
                    setSaveAlert('success');
                    setTimeout(() => {
                        onClose?.();
                    }, 1000);
                },
                // Stays open on a partial save, so the switches can be retried.
                onError: (error) => setSaveAlert(error instanceof NotificationsNotSavedError ? 'warning' : 'error'),
            }
        );
    };

    return (
        <div className={compact ? 'flex flex-1 flex-col gap-4 overflow-y-auto md:overflow-visible' : 'modal__content'}>
            <Snackbar open={saveAlert !== null} autoHideDuration={6000} onClose={handleCloseSaveAlert}>
                <Alert onClose={handleCloseSaveAlert} severity={saveAlert ?? 'success'}>
                    {saveAlert === 'error'
                        ? tNotify('saveFailed')
                        : saveAlert === 'warning'
                          ? tNotify('savePartial')
                          : 'Save successfully!'}
                </Alert>
            </Snackbar>
            <form className={compact ? 'flex flex-col gap-4' : 'modal__form'} id='alarmForm'>
                {!compact && <div className='title'>{t('configure')}</div>}

                <Section heading={t('failAlarm')}>
                    {/* Enabling working hours splits thresholds into two sets; off = base set applies all day. */}
                    <ToggleRow
                        label={t('enableWorkingHours')}
                        checked={workingEnabled}
                        onChange={(enabled: boolean) => {
                            setWorkingEnabled(enabled);
                            setActiveShift(enabled ? 'working' : 'nonWorking');
                        }}
                    />

                    {/* Window + days define when the working set applies; the tab switch
                        below picks which threshold group to edit. All three only make
                        sense when working-hours is enabled — off = a single threshold
                        set (non-working) applies all day, so no switch to render. */}
                    {workingEnabled && (
                        <>
                            {/* Working days first — coarser "which days" precedes the finer
                                "which hours on those days" window below. */}
                            <WorkDaysToggle value={workDays} onChange={setWorkDays} />
                            <WorkTimeRangeSlider
                                start={workStart}
                                end={workEnd}
                                timezone={effectiveTimezone}
                                onChange={({ start, end }: any) => {
                                    setWorkStart(start);
                                    setWorkEnd(end);
                                }}
                            />
                        </>
                    )}

                    {/* Switch pill sits with the threshold rows below (both uncarded), not
                        with the window/days cards above — semantically the switch selects
                        which set of thresholds to edit, so it belongs to that group. */}
                    {workingEnabled && (
                        <div className='flex w-full gap-3 rounded-full bg-slate-200/60 p-1'>
                            <button
                                id='working'
                                type='button'
                                className={`flex-1 rounded-full px-3 py-2 text-sm font-semibold transition ${activeShift === 'working' ? 'bg-blue-500 text-white shadow-sm' : 'text-slate-600'}`}
                                onClick={() => setActiveShift('working')}
                            >
                                {t('workingHours')}
                            </button>
                            <button
                                id='nonWorking'
                                type='button'
                                className={`flex-1 rounded-full px-3 py-2 text-sm font-semibold transition ${activeShift === 'nonWorking' ? 'bg-blue-500 text-white shadow-sm' : 'text-slate-600'}`}
                                onClick={() => setActiveShift('nonWorking')}
                            >
                                {t('nonWorkingHours')}
                            </button>
                        </div>
                    )}

                    <ThresholdGroup
                        visible={activeShift === 'working'}
                        namePrefix='work'
                        upperRef={workUpLimitInput}
                        lowerRef={workLowLimitInput}
                        timeRef={workTimeLimitInput}
                        upperDefault={workingHours.upperLimit ?? record.upperLimit}
                        lowerDefault={workingHours.lowerLimit ?? record.lowerLimit}
                        timeDefault={workThresholdConvert.value}
                        upperUnit={upperUnit}
                        lowerUnit={lowerUnit}
                        onUpperUnit={setUpperUnit}
                        onLowerUnit={setLowerUnit}
                        timeUnit={workTimeUnit}
                        onTimeUnit={setWorkTimeUnit}
                    />

                    <ThresholdGroup
                        visible={activeShift === 'nonWorking'}
                        namePrefix='off'
                        upperRef={offUpLimitInput}
                        lowerRef={offLowLimitInput}
                        timeRef={offTimeLimitInput}
                        upperDefault={record.upperLimit}
                        lowerDefault={record.lowerLimit}
                        timeDefault={offThresholdConvert.value}
                        upperUnit={upperUnit}
                        lowerUnit={lowerUnit}
                        onUpperUnit={setUpperUnit}
                        onLowerUnit={setLowerUnit}
                        timeUnit={offTimeUnit}
                        onTimeUnit={setOffTimeUnit}
                    />
                </Section>

                <Section heading={t('dataLogging')}>
                    <FieldRow label={t('loggingInterval')}>
                        <ControlGroup>
                            <input
                                key='interval'
                                name='interval'
                                disabled
                                className='flex-1 min-w-0 rounded-full border border-slate-300 bg-slate-100 px-4 py-2 text-sm font-medium text-slate-500'
                                type='number'
                                placeholder='1'
                                defaultValue={intervalConvert.value}
                                ref={intervalInput}
                            />
                            <StaticTimeToggle unit={intervalUnit} />
                        </ControlGroup>
                    </FieldRow>
                </Section>

                <Section heading={t('offlineNotification')}>
                    <p className='text-xs text-slate-400'>{t('offlineNotificationDesc')}</p>
                    <OfflineStepper minutes={offlineMinutes} onChange={setOfflineMinutes} />
                </Section>

                <Section heading={t('alarmNotification')}>
                    <ToggleRow label={t('inAppNotification')} checked={inAppNotification} onChange={handleToggleApp} />
                    <ToggleRow label={t('line')} checked={lineEnabled} onChange={handleToggleLine} />
                    <div
                        className='rounded-full border border-slate-200 bg-white overflow-hidden'
                        style={emailEnabled ? { borderRadius: '1rem' } : {}}
                    >
                        <div className='flex items-center justify-between px-4 py-2.5'>
                            <span className='text-sm font-medium text-slate-500'>{t('email')}</span>
                            <button
                                type='button'
                                role='switch'
                                aria-checked={emailEnabled}
                                aria-label={t('email')}
                                onClick={() => handleToggleEmail(!emailEnabled)}
                                className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out ${emailEnabled ? 'bg-blue-500' : 'bg-slate-300'}`}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${emailEnabled ? 'translate-x-6' : 'translate-x-1'} mt-1`}
                                />
                            </button>
                        </div>
                        {emailEnabled && (
                            <div className='flex flex-col gap-2 px-4 pb-4'>
                                <input
                                    key='email'
                                    name='email'
                                    className='w-full rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 focus:border-blue-400 focus:outline-none'
                                    type='text'
                                    placeholder='email@example.com'
                                    defaultValue={record.notifyEmails?.[0] ?? record.email}
                                    ref={emailInput}
                                />
                                <input
                                    name='email2'
                                    className='w-full rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 focus:border-blue-400 focus:outline-none'
                                    type='text'
                                    placeholder='email@example.com'
                                    defaultValue={record.notifyEmails?.[1] ?? ''}
                                    ref={emailInput2}
                                />
                                <input
                                    name='email3'
                                    className='w-full rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 focus:border-blue-400 focus:outline-none'
                                    type='text'
                                    placeholder='email@example.com'
                                    defaultValue={record.notifyEmails?.[2] ?? ''}
                                    ref={emailInput3}
                                />
                            </div>
                        )}
                    </div>
                </Section>

                {onToggleArchive && (
                    <Section heading={t('deviceStatus')}>
                        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                            <p className='text-sm text-slate-500'>
                                {isArchived ? t('hiddenFromDashboard') : t('hideFromDashboard')}
                            </p>
                            <button
                                type='button'
                                disabled={archiving}
                                onClick={onToggleArchive}
                                className={`flex-shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 ${
                                    isArchived
                                        ? 'border-blue-400 text-blue-500 hover:bg-blue-50'
                                        : 'border-[#E26A6A] text-[#E26A6A] hover:bg-[#fdeded]'
                                }`}
                            >
                                {archiving ? '...' : isArchived ? t('backToActive') : t('moveToArchived')}
                            </button>
                        </div>
                    </Section>
                )}
            </form>
            <div
                className={compact ? 'flex justify-end pt-2' : 'button__container'}
                style={compact ? undefined : { marginTop: '2rem' }}
            >
                <button
                    className={
                        compact
                            ? 'rounded-full bg-blue-500 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-600 cursor-pointer'
                            : 'save__all'
                    }
                    onClick={handleSaveAll}
                >
                    {t('saveAll')}
                </button>
            </div>
        </div>
    );
}

export default AlarmConfigForm;

function Section({ heading, children }: any) {
    return (
        <section className='flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-inner'>
            <div className='text-sm font-semibold text-slate-600'>{heading}</div>
            <div className='flex flex-col gap-3'>{children}</div>
        </section>
    );
}

// Stepper for the offline-notification delay. Value is minutes, always a multiple of
// OFFLINE_STEP_MIN (5), min one step. Shown as a readable "Hh Mm" duration.
function OfflineStepper({ minutes, onChange }: any) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const label = [hours ? `${hours}h` : null, mins || !hours ? `${mins}m` : null].filter(Boolean).join(' ');

    const step = (delta: number) => onChange(Math.max(OFFLINE_STEP_MIN, minutes + delta));

    return (
        <div className='flex items-center justify-between rounded-full border border-slate-200 bg-white px-2 py-1.5'>
            <button
                type='button'
                aria-label='decrease'
                onClick={() => step(-OFFLINE_STEP_MIN)}
                disabled={minutes <= OFFLINE_STEP_MIN}
                className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-lg font-semibold text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40'
            >
                −
            </button>
            <span className='text-sm font-semibold tabular-nums text-slate-700'>{label}</span>
            <button
                type='button'
                aria-label='increase'
                onClick={() => step(OFFLINE_STEP_MIN)}
                className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-lg font-semibold text-slate-600 transition hover:bg-slate-100'
            >
                +
            </button>
        </div>
    );
}

// An inline, click-to-edit HH:MM field. Shows the time as a button; clicking turns
// it into a text input the user can type into (Enter/blur commits, Esc cancels).
function TimeField({ label, value, align, onCommit }: any) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (editing) inputRef.current?.select();
    }, [editing]);

    const begin = () => {
        setDraft(formatClock(value));
        setEditing(true);
    };
    const commit = () => {
        const parsed = parseClock(draft);
        if (parsed != null) onCommit(parsed);
        setEditing(false);
    };

    return (
        <div className={`flex flex-col ${align === 'right' ? 'items-end' : 'items-start'}`}>
            <span className='text-[10px] font-semibold uppercase tracking-wide text-slate-400'>{label}</span>
            {editing ? (
                <input
                    ref={inputRef}
                    value={draft}
                    inputMode='numeric'
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            commit();
                        } else if (e.key === 'Escape') {
                            // Keep Escape local — otherwise it bubbles to the modal and closes it.
                            e.stopPropagation();
                            setEditing(false);
                        }
                    }}
                    className={`w-20 rounded-lg border border-blue-300 px-2 py-0.5 text-lg font-bold tabular-nums text-slate-700 focus:border-blue-400 focus:outline-none ${align === 'right' ? 'text-right' : ''}`}
                />
            ) : (
                <button
                    type='button'
                    onClick={begin}
                    className='rounded-lg text-lg font-bold tabular-nums text-slate-700 transition hover:text-blue-500'
                >
                    {formatClock(value)}
                </button>
            )}
        </div>
    );
}

// A flattened version of the Apple Clock sleep dial: a horizontal 24h track with
// two draggable handles marking the start and end of the working-time window.
// Supports windows that wrap past midnight (e.g. 22:00–06:00).
function WorkTimeRangeSlider({ start, end, timezone, onChange }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime.Edit' });
    const trackRef = useRef<HTMLDivElement | null>(null);
    const draggingRef = useRef<string | null>(null);

    const pct = (minutes: number) => (minutes / DAY_MINUTES) * 100;
    const wraps = start > end;

    const minutesFromClientX = (clientX: number) => {
        const rect = trackRef.current!.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        return Math.round((ratio * DAY_MINUTES) / WINDOW_STEP) * WINDOW_STEP;
    };

    const setHandle = (which: string, minutes: number) => {
        if (which === 'start') onChange({ start: minutes, end });
        else onChange({ start, end: minutes });
    };

    const handlePointerDown = (which: string) => (event: any) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        draggingRef.current = which;
    };
    const handlePointerMove = (which: string) => (event: any) => {
        if (draggingRef.current !== which) return;
        setHandle(which, minutesFromClientX(event.clientX));
    };
    const handlePointerUp = () => {
        draggingRef.current = null;
    };
    const handleKeyDown = (which: string, minutes: number) => (event: any) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            event.preventDefault();
            setHandle(which, Math.max(0, minutes - WINDOW_STEP));
        } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            event.preventDefault();
            setHandle(which, Math.min(DAY_MINUTES, minutes + WINDOW_STEP));
        }
    };

    // Rendered as plain functions (not nested components) so the button/div keep a
    // stable element identity across re-renders — otherwise a drag would remount the
    // handle mid-gesture and drop its pointer capture.
    const renderHandle = (which: string, minutes: number) => (
        <button
            key={which}
            type='button'
            role='slider'
            aria-label={which === 'start' ? t('workStart') : t('workEnd')}
            aria-valuemin={0}
            aria-valuemax={DAY_MINUTES}
            aria-valuenow={minutes}
            aria-valuetext={formatClock(minutes)}
            onPointerDown={handlePointerDown(which)}
            onPointerMove={handlePointerMove(which)}
            onPointerUp={handlePointerUp}
            onKeyDown={handleKeyDown(which, minutes)}
            className='absolute top-1/2 z-10 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 cursor-grab touch-none items-center justify-center rounded-full border-2 border-blue-500 bg-white shadow-md active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-blue-400'
            style={{ left: `${pct(minutes)}%` }}
        >
            <span className='h-1.5 w-1.5 rounded-full bg-blue-500' />
        </button>
    );

    const renderHighlight = (key: string, from: number, to: number) => (
        <div
            key={key}
            className='absolute top-0 h-full rounded-full bg-blue-500/80'
            style={{ left: `${pct(from)}%`, width: `${pct(to - from)}%` }}
        />
    );

    return (
        <div className='flex flex-col gap-3 rounded-2xl bg-white px-4 py-4'>
            <div className='flex items-start justify-between'>
                <TimeField
                    label={t('workStart')}
                    value={start}
                    align='left'
                    onCommit={(m: number) => onChange({ start: m, end })}
                />
                <TimeField
                    label={t('workEnd')}
                    value={end}
                    align='right'
                    onCommit={(m: number) => onChange({ start, end: m })}
                />
            </div>

            {/* Zone caption — makes clear that "09:00" is wall-clock in this IANA zone,
                not the viewer's browser time. Same value the save payload ships. */}
            {timezone && (
                <div className='text-[11px] font-medium text-slate-400'>
                    {t('timezone')}: {formatTimezoneDisplay(timezone)}
                </div>
            )}

            <div className='px-3'>
                <div ref={trackRef} className='relative h-2.5 rounded-full bg-slate-200'>
                    {wraps
                        ? [renderHighlight('seg-start', start, DAY_MINUTES), renderHighlight('seg-end', 0, end)]
                        : renderHighlight('seg', start, end)}
                    {renderHandle('start', start)}
                    {renderHandle('end', end)}
                </div>

                <div className='mt-2 flex justify-between text-[10px] font-medium text-slate-400'>
                    <span>0</span>
                    <span>6</span>
                    <span>12</span>
                    <span>18</span>
                    <span>24</span>
                </div>
            </div>
        </div>
    );
}

// Which days count as working days. Shared setting, does not switch with the tabs.
function WorkDaysToggle({ value, onChange }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime.Edit' });
    const options = [
        { key: 'weekdays', label: t('monToFri') },
        { key: 'everyday', label: t('monToSun') },
    ];

    return (
        <div className='flex flex-col gap-2 rounded-2xl bg-white px-4 py-4'>
            <span className='text-[10px] font-semibold uppercase tracking-wide text-slate-400'>{t('workDays')}</span>
            <div className='flex w-full gap-3 rounded-full bg-slate-200/60 p-1'>
                {options.map(({ key, label }) => (
                    <button
                        key={key}
                        type='button'
                        className={`flex-1 rounded-full px-3 py-2 text-sm font-semibold transition ${value === key ? 'bg-blue-500 text-white shadow-sm' : 'text-slate-600'}`}
                        onClick={() => onChange(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function ThresholdGroup({
    visible,
    namePrefix,
    upperRef,
    lowerRef,
    timeRef,
    upperDefault,
    lowerDefault,
    timeDefault,
    upperUnit,
    lowerUnit,
    onUpperUnit,
    onLowerUnit,
    timeUnit,
    onTimeUnit,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime.Edit' });

    // Both groups stay mounted so their uncontrolled inputs keep their values
    // across tab switches; the inactive group is hidden rather than unmounted.
    return (
        <div className={visible ? 'flex flex-col gap-3' : 'hidden'}>
            <FieldRow label={t('upperLimit')}>
                <ControlGroup>
                    <input
                        name={`${namePrefix}UpLimit`}
                        className='flex-1 min-w-0 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 focus:border-blue-400 focus:outline-none'
                        type='number'
                        placeholder={convertTemperature(8, upperUnit)}
                        defaultValue={upperDefault}
                        ref={upperRef}
                    />
                    <UnitToggle currentUnit={upperUnit} onSelect={onUpperUnit} />
                </ControlGroup>
            </FieldRow>

            <FieldRow label={t('lowerLimit')}>
                <ControlGroup>
                    <input
                        name={`${namePrefix}LowLimit`}
                        className='flex-1 min-w-0 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 focus:border-blue-400 focus:outline-none'
                        type='number'
                        placeholder={convertTemperature(2, lowerUnit)}
                        defaultValue={lowerDefault}
                        ref={lowerRef}
                    />
                    <UnitToggle currentUnit={lowerUnit} onSelect={onLowerUnit} />
                </ControlGroup>
            </FieldRow>

            <FieldRow label={t('duration')}>
                <ControlGroup>
                    <input
                        name={`${namePrefix}TimeLimit`}
                        className='flex-1 min-w-0 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 focus:border-blue-400 focus:outline-none'
                        type='number'
                        placeholder='10'
                        defaultValue={timeDefault}
                        ref={timeRef}
                    />
                    <TimeToggle unit={timeUnit} onSelect={onTimeUnit} />
                </ControlGroup>
            </FieldRow>
        </div>
    );
}

function FieldRow({ label, children }: any) {
    return (
        <div className='flex flex-col gap-2'>
            <label className='text-xs font-semibold uppercase tracking-wide text-slate-400'>{label}</label>
            <div className='flex flex-col gap-2'>{children}</div>
        </div>
    );
}

function ControlGroup({ children }: any) {
    return <div className='flex w-full flex-wrap items-center gap-3 md:flex-nowrap'>{children}</div>;
}

function UnitToggle({ currentUnit, onSelect }: any) {
    return (
        <div className='flex flex-shrink-0 items-center gap-2 rounded-full border border-slate-200 bg-white p-1'>
            <button
                type='button'
                className={`h-8 w-10 rounded-full text-sm font-semibold transition cursor-pointer ${currentUnit === 'celsius' ? 'bg-blue-500 text-white shadow-sm' : 'text-slate-500'}`}
                onClick={() => onSelect('celsius')}
            >
                °C
            </button>
            <button
                type='button'
                className={`h-8 w-10 rounded-full text-sm font-semibold transition cursor-pointer ${currentUnit === 'fahrenheit' ? 'bg-blue-500 text-white shadow-sm' : 'text-slate-500'}`}
                onClick={() => onSelect('fahrenheit')}
            >
                °F
            </button>
        </div>
    );
}

function TimeToggle({ unit, onSelect }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime.Edit' });
    const items = [
        { key: 'second', label: t('S') },
        { key: 'minute', label: t('M') },
        { key: 'hour', label: t('H') },
        { key: 'day', label: t('D') },
    ];

    return (
        <div className='flex flex-shrink-0 items-center gap-2 rounded-full border border-slate-200 bg-white p-1'>
            {items.map(({ key, label }: any) => (
                <button
                    key={key}
                    type='button'
                    className={`h-8 w-8 rounded-full text-xs font-semibold transition cursor-pointer ${unit === key ? 'bg-blue-500 text-white shadow-sm' : 'text-slate-500'}`}
                    onClick={() => onSelect(key)}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}

function ToggleRow({ label, checked, onChange }: any) {
    return (
        <div className='flex items-center justify-between rounded-full border border-slate-200 bg-white px-4 py-2.5'>
            <span className='text-sm font-medium text-slate-500'>{label}</span>
            <button
                type='button'
                role='switch'
                aria-checked={checked}
                aria-label={label}
                onClick={() => onChange(!checked)}
                className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out ${checked ? 'bg-blue-500' : 'bg-slate-300'}`}
            >
                <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-6' : 'translate-x-1'} mt-1`}
                />
            </button>
        </div>
    );
}

function StaticTimeToggle({ unit }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime.Edit' });
    const items = [
        { key: 'second', label: t('S') },
        { key: 'minute', label: t('M') },
        { key: 'hour', label: t('H') },
        { key: 'day', label: t('D') },
    ];

    return (
        <div className='flex flex-shrink-0 items-center gap-2 rounded-full border border-slate-200 bg-white p-1'>
            {items.map(({ key, label }: any) => (
                <button
                    key={key}
                    type='button'
                    disabled
                    className={`h-8 w-8 rounded-full text-xs font-semibold cursor-not-allowed ${unit === key ? 'bg-blue-500 text-white' : 'text-slate-400'}`}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}
